import React, {
  createContext,
  useCallback,
  useState,
  useContext,
  useEffect,
} from 'react';
import api from '../services/api';

import { IOption } from '../components/SelectSync';
import { useToast } from './toast';
import getResponseErrors from '../utils/getResponseErrors';

interface IImagemClinica {
  id: number;
  nome: string;
  arquivo_url: string;
}

export interface IConvenio {
  ConvCodigo: number;
  ConvNome: string;
  ConvPagaCaixa: boolean;
  ConvGeraGuia: boolean;
}
interface IRetornoConvenio {
  data: IConvenio[];
  count: number;
}

interface ClinicaContextData {
  loading: boolean;
  optionsClinica: IOption[];
  convenios: IConvenio[];
  optionsConvenio: IOption[];
  imagensClinica: IImagemClinica[];
  dataHoraServidor: Date;
  buscaClinicas(): Promise<void>;
  buscaConvenios(): Promise<void>;
  limparClinica(): void;
}

const ClinicaContext = createContext<ClinicaContextData>(
  {} as ClinicaContextData,
);

const ClinicaProvider: React.FC = ({ children }) => {
  const [loading, setLoading] = useState(false);

  const { addToast } = useToast();

  const [optionsClinica, setOptionsClinica] = useState<IOption[]>([]);
  const [optionsConvenio, setOptionsConvenio] = useState<IOption[]>([]);
  const [convenios, setConvenios] = useState<IConvenio[]>([]);
  const [imagensClinica, setImagensClinica] = useState<IImagemClinica[]>([]);
  const [dataHoraServidor, setDataHoraServidor] = useState<Date>(
    new Date(1980, 1, 1, 4),
  );

  const buscaClinicas = useCallback(async () => {
    setLoading(true);
    try {
      const optionClinicas = localStorage.getItem(
        '@AgendaFacil:optionClinicas',
      );

      if (optionClinicas) {
        setOptionsClinica(JSON.parse(optionClinicas));
      } else {
        const response = await api.get<IOption[]>('clinicas/combo');

        localStorage.setItem(
          '@AgendaFacil:optionClinicas',
          JSON.stringify(response.data),
        );
        setOptionsClinica(response.data);
      }

      const imagens = localStorage.getItem('@AgendaFacil:imagensClinica');
      if (imagens) {
        setImagensClinica(JSON.parse(imagens));
      } else {
        const response = await api.get<IImagemClinica[]>('clinicas/imagens');

        localStorage.setItem(
          '@AgendaFacil:imagensClinica',
          JSON.stringify(response.data),
        );
        setImagensClinica(response.data);
      }

      const response = await api.get('clinicas/datahora');

      setDataHoraServidor(response.data.dataHora);
    } catch (error) {
      const mensagemErro = getResponseErrors(error);
      addToast({
        title: 'Não foi possível carregar dados da clinica',
        type: 'error',
        description: mensagemErro,
      });
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    buscaClinicas();
  }, [buscaClinicas]);

  const buscaConvenios = useCallback(async () => {
    setLoading(true);
    try {
      const conveniosLocal = localStorage.getItem('@AgendaFacil:Convenios');
      if (conveniosLocal && JSON.parse(conveniosLocal).length > 0) {
        const conveniosArray: IConvenio[] = JSON.parse(conveniosLocal);
        const optionsFormatado = conveniosArray.map(con => {
          return {
            label: con.ConvNome,
            value: con.ConvCodigo.toString(),
          };
        });
        setConvenios(conveniosArray);
        setOptionsConvenio(optionsFormatado);
      } else {
        const response = await api.get<IRetornoConvenio>(
          'convenios?ConvAtivo=true',
        );

        const optionsFormatado = response.data.data.map(con => {
          return {
            label: con.ConvNome,
            value: con.ConvCodigo.toString(),
          };
        });
        localStorage.setItem(
          '@AgendaFacil:Convenios',
          JSON.stringify(response.data.data),
        );
        setConvenios(response.data.data);
        setOptionsConvenio(optionsFormatado);
      }
    } catch (error) {
      const mensagemErro = getResponseErrors(error);
      addToast({
        title: 'Não foi possível carregar dados dos convenios',
        type: 'error',
        description: mensagemErro,
      });
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  const limparClinica = useCallback(() => {
    localStorage.removeItem('@AgendaFacil:optionClinicas');
    localStorage.removeItem('@AgendaFacil:imagensClinica');
    localStorage.removeItem('@AgendaFacil:dataHora');
    localStorage.removeItem('@AgendaFacil:Convenios');
    setOptionsClinica([]);
    setImagensClinica([]);
    setOptionsConvenio([]);
    setConvenios([]);
    setDataHoraServidor(new Date(1980, 1, 1, 4));
  }, []);

  return (
    <ClinicaContext.Provider
      value={{
        loading,
        optionsClinica,
        dataHoraServidor,
        imagensClinica,
        buscaClinicas,
        limparClinica,
        buscaConvenios,
        optionsConvenio,
        convenios,
      }}
    >
      {children}
    </ClinicaContext.Provider>
  );
};

function useClinica(): ClinicaContextData {
  const context = useContext(ClinicaContext);

  if (!context) {
    throw new Error('useClinica deve ser usado com o ClinicaProvider');
  }

  return context;
}

export { ClinicaProvider, useClinica };
